// The ship's interior lighting in the browser (shared/lighting.ts; Grey, 2026-10-22): the colour temperature the Captain sets,
// faded in over a second and a half, on the room lights and the ship's white light fixtures; and the emergency lighting (no power
// to the lighting, a sabotaged compartment) laid over it, so the two never fight over the same light.
// What it touches: the scene's hemispheric fill (diffuse and ground colour) and the "sun" (diffuse, specular), and the emissive
// colour of the white fixtures (isRoomFixture: corridor strips, ceiling panels). Never their intensities (the render quality and
// the theater's house lights own those), never a screen, hologram, sign, status or alert light.
// A room's own light (its warmth or colour, and a printed light switch) is that room's alone (Grey, 2026-11-03: "Light from quarters
// are leaking into corridors"): it isn't laid on the scene's lights any more, which lit everything you could see, but handed to
// client/roomfield.ts, where each surface takes the light of the room it's in, wherever you stand.
// Cost: colours are written once per change, or each frame of a fade (a dozen materials, two lights and a row of rooms); idle, a frame
// costs a few comparisons.
import type { Color3, Material, Scene } from "@babylonjs/core";
import { LIGHT_K_DEFAULT, cleanKelvin, isRoomFixture, lightTint, toneTint, type RoomTone } from "../shared/lighting.js";
import type { RGBK, RoomField } from "./roomfield.js";
const CORR_DARK: RGB = [1, 0.25, 0.2], CORR_EMERGENCY: RGB = [0.6, 0.14, 0.12];   // the corridors' strips with no power to the lighting, or in a sabotaged section

type RGB = [number, number, number];
const FADE_MS = 1500;
const ROOM_RATE = 3;                                   // a room's light settling on a change: most of the way in a second (as the light switches always did)
const GROUND: RGB = [0.18, 0.2, 0.26];                 // the hemispheric light's ground colour as built
const EMERGENCY: RGB = [1, 0.42, 0.38];                // no power to the lighting: dim and red (the intensities drop in client/main.ts)
const DARK_SKY: RGB = [1, 0.4, 0.3], DARK_GROUND: RGB = [0.28, 0.08, 0.06];   // a sabotaged compartment, fully dark: the same red as before
const lerp = (a: RGB, b: RGB, t: number): RGB => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const same = (a: RGB, b: RGB) => Math.abs(a[0] - b[0]) < 1e-4 && Math.abs(a[1] - b[1]) < 1e-4 && Math.abs(a[2] - b[2]) < 1e-4;

interface Lit { diffuse: Color3; specular: Color3 }
interface Hemi extends Lit { groundColor: Color3 }
interface Glowing { emissiveColor: Color3; name: string }

export class RoomLight {
  kelvin = LIGHT_K_DEFAULT;                            // the ship's setting, as the server last sent it
  private shown: RGB = [1, 1, 1];                      // the ship's tint on the lights and fixtures now (mid-fade: part way)
  private from: RGB = [1, 1, 1]; private to: RGB = [1, 1, 1]; private t0 = 0; private fading = false; private heard = false;
  private lit = true; private dark = 0; private outside = false;
  private fixtures = new Map<Glowing, RGB>();          // each white fixture's own emissive colour as built
  constructor(private scene: Scene, private hemi: Hemi, private sun: Lit, private field: RoomField | null = null) {}

  // the server's setting: faded in (the first one after joining is put straight up: the rooms were already that way)
  setKelvin(k: number) {
    const kk = cleanKelvin(k), first = !this.heard; this.heard = true;
    if (kk === this.kelvin && !first) return; this.kelvin = kk;
    const to = lightTint(kk);
    if (first) { this.fading = false; this.shown = to; this.to = to; this.apply(); return; }
    this.from = [...this.shown]; this.to = to; this.t0 = performance.now(); this.fading = true;
  }
  // the rooms with a light of their own (shared/lighting.ts RoomTone: a warmth or a colour) and the rooms a printed switch has turned down,
  // as the server last sent them: each room's light is its own (client/roomfield.ts), faded in
  private tones: Record<string, RoomTone> = {}; private switches: Record<string, number> = {};
  private roomShown = new Map<string, RGBK>(); private roomsMoving = true; private roomsAt = 0;
  setRooms(tones: Record<string, RoomTone>, switches: Record<string, number>) {
    if (tones === this.tones && switches === this.switches) return;
    this.tones = tones; this.switches = switches; this.roomsMoving = true;
  }
  // a room's light against the ship's, as the room's surfaces take it: its colour over the ship's (none with no power to the lighting,
  // fading out as a sabotaged compartment goes dark, and none seen from outside), and its switch
  private roomWant(name: string): RGBK {
    const t = this.lit && !this.outside ? toneTint(this.tones[name]) : null, s = this.switches[name] ?? 1, k = this.outside ? 1 : Math.max(0, Math.min(1, s));
    if (!t) return [1, 1, 1, k];
    const S = this.shown, d = this.dark, r: RGB = [t[0] / Math.max(S[0], 1e-3), t[1] / Math.max(S[1], 1e-3), t[2] / Math.max(S[2], 1e-3)];
    return [r[0] + (1 - r[0]) * d, r[1] + (1 - r[1]) * d, r[2] + (1 - r[2]) * d, k];
  }
  private roomsTick() {
    if (!this.field || (!this.roomsMoving && !this.fading)) return;
    const now = performance.now(), dt = this.roomsAt ? Math.min(0.1, (now - this.roomsAt) / 1000) : 1; this.roomsAt = now;
    const a = 1 - Math.exp(-ROOM_RATE * dt), names = new Set([...Object.keys(this.tones), ...Object.keys(this.switches), ...this.roomShown.keys()]);
    let moving = false;
    for (const n of names) {
      const want = this.roomWant(n), was = this.roomShown.get(n) ?? [1, 1, 1, 1];
      const next = was.map((v, i) => { const x = v + (want[i] - v) * a; return Math.abs(want[i] - x) < 0.002 ? want[i] : x; }) as RGBK;
      if (next.some((v, i) => v !== want[i])) moving = true;
      if (next.every((v) => v === 1) && want.every((v) => v === 1)) this.roomShown.delete(n); else this.roomShown.set(n, next);
    }
    this.roomsMoving = moving; this.field.set(this.roomShown);
  }
  roomsNow() { return Object.fromEntries([...this.roomShown].map(([n, v]) => [n, v.map((x) => +x.toFixed(3))])); }   // (tests)
  // the corridors' strips and their light (client/corridorlights.ts): the ship's lighting, never a room's, and the emergency over it
  corridor(): RGB { return !this.lit ? CORR_EMERGENCY : lerp(lightTint(this.kelvin), CORR_DARK, this.dark); }
  // the emergency lighting over it: lit (the lighting has power) and dark (0..1, a sabotaged compartment fading in)
  setEmergency(lit: boolean, dark: number) { if (lit === this.lit && dark === this.dark) return; this.lit = lit; this.dark = dark; this.roomsMoving = true; this.apply(); }
  // the ship seen from outside: the scene's lights are the hull's then, so they go back to white (at once: a camera change, not a fade)
  setOutside(o: boolean) { if (o === this.outside) return; this.outside = o; this.roomsMoving = true; this.apply(); }
  // the ship's materials are in (or more have come): find the white fixtures, keep each one's own colour, and tint them
  rescan() {
    for (const m of this.scene.materials) {
      const g = m as unknown as Partial<Glowing>;
      if (!g.emissiveColor || this.fixtures.has(g as Glowing) || !isRoomFixture(m.name, g.emissiveColor)) continue;
      this.fixtures.set(g as Glowing, [g.emissiveColor.r, g.emissiveColor.g, g.emissiveColor.b]); this.field?.markFixture(m as Material);
    }
    const t = this.shown; for (const [mt, b] of this.fixtures) mt.emissiveColor.set(b[0] * t[0], b[1] * t[1], b[2] * t[2]);
  }
  get fixtureCount() { return this.fixtures.size; }
  // each frame: only while a fade runs is there anything to do
  tick() {
    if (this.fading) {
      const u = Math.min(1, (performance.now() - this.t0) / FADE_MS), s = u * u * (3 - 2 * u);
      this.shown = u >= 1 ? [...this.to] : lerp(this.from, this.to, s); if (u >= 1) this.fading = false;
      this.apply(); this.roomsMoving = true;
    }
    this.roomsTick(); this.field?.tick();
  }
  private lastFix: RGB = [1, 1, 1];
  private apply() {
    const t = this.shown, L: RGB = this.outside ? [1, 1, 1] : t, d = this.dark;
    const sky = this.lit ? lerp(L, DARK_SKY, d) : EMERGENCY, ground = lerp([GROUND[0] * L[0], GROUND[1] * L[1], GROUND[2] * L[2]], DARK_GROUND, d);
    this.hemi.diffuse.set(sky[0], sky[1], sky[2]); this.hemi.groundColor.set(ground[0], ground[1], ground[2]);
    this.sun.diffuse.set(L[0], L[1], L[2]); this.sun.specular.set(L[0], L[1], L[2]);
    if (same(t, this.lastFix)) return; this.lastFix = [...t];     // the fixtures only when their tint moved
    for (const [mt, b] of this.fixtures) mt.emissiveColor.set(b[0] * t[0], b[1] * t[1], b[2] * t[2]);
  }
}
